// Permission checking utilities based on role assignments

export interface UserRole {
  role_id: number;
  role_name: string;
  role_description?: string;
  /**
   * Flat list of permission slugs attached to this role,
   * e.g. ["view_orders", "edit_orders", "view_users"]
   */
  permissions: string[];
  /**
   * The exact list of routes this role's permission groups cover.
   * Pre-computed on login by the backend — avoids slug-collision bugs.
   * e.g. ["/orders", "/entities", "/"]
   */
  allowed_routes?: string[];
}

/**
 * Check if a user with a given role has a specific permission slug.
 * Falls back to allowing everything when no role is set to avoid
 * accidentally locking out existing users during migration.
 */
export const hasPermission = (
  role: UserRole | null,
  permissionSlug: string,
): boolean => {
  // During migration: if no role yet, keep previous behaviour (show everything)
  if (!role) return true;

  const roleName = role.role_name?.toLowerCase() || "";
  if (roleName === "super admin") return true;

  const perms = role.permissions || [];
  return perms.includes(permissionSlug);
};

/**
 * Check if the current role can access a given route.
 *
 * Uses allowed_routes (pre-computed by backend at login) when available —
 * this is an exact list of routes the role's permission groups cover, so
 * there is no risk of slug-collision granting unintended access.
 *
 * Falls back to slug-matching against routePermissions for backwards compat.
 *
 * - Super Admin can access everything.
 * - Routes not covered by any of the role's permission groups are DENIED.
 */
export const canAccessRoute = (
  role: UserRole | null,
  route: string,
  routePermissions: Record<string, string[]> = {},
): boolean => {
  // During migration: if no role yet, keep previous behaviour (show everything)
  if (!role) return true;

  const roleName = role.role_name?.toLowerCase() || "";
  if (roleName === "super admin") return true;

  // Preferred: use the exact allowed_routes list from the backend
  if (role.allowed_routes && role.allowed_routes.length > 0) {
    return role.allowed_routes.includes(route);
  }

  // Fallback: slug matching (used if allowed_routes not populated yet)
  const required = routePermissions[route];
  // DENY by default — routes not in the DB map are Super Admin only
  if (!required || required.length === 0) {
    return false;
  }

  const perms = role.permissions || [];
  return required.some((slug) => perms.includes(slug));
};

/**
 * Utility to get all routes a role can access based on its permissions.
 */
export const getAllowedRoutes = (
  role: UserRole | null,
  routePermissions: Record<string, string[]> = {},
): string[] => {
  if (!role) {
    // No role yet: allow all routes (legacy behaviour)
    return Object.keys(routePermissions);
  }

  // If allowed_routes is available, use it directly
  if (role.allowed_routes && role.allowed_routes.length > 0) {
    return role.allowed_routes;
  }

  return Object.keys(routePermissions).filter((route) =>
    canAccessRoute(role, route, routePermissions),
  );
};
